import { Vec3 } from '@/core/math/Vec3';
import { CubeFace, directionToFace, faceToDirection, projectOntoFace } from '@/frames/PlanetaryCoordinates';

/**
 * Cube-sphere quadtree addressing.
 *
 *   cube -> six faces -> quadtree subdivision -> spherical projection ->
 *   height displacement
 *
 * A chunk is identified by (face, level, ix, iy). That identity is stable and
 * purely spatial: it does not depend on traversal order, camera history or when
 * the chunk was created. The same chunk id always covers the same patch of
 * planet, which is what lets terrain deltas be persisted against chunk ids and
 * still line up after a reload.
 */

export interface ChunkId {
  face: CubeFace;
  level: number;
  ix: number;
  iy: number;
}

export function chunkKey(id: ChunkId): string {
  return `${id.face}:${id.level}:${id.ix}:${id.iy}`;
}

export function parseChunkKey(key: string): ChunkId {
  const [face, level, ix, iy] = key.split(':').map(Number);
  return { face: face as CubeFace, level, ix, iy };
}

/** Number of chunks along one edge of a face at a given level. */
export function tilesPerEdge(level: number): number {
  return 1 << level;
}

/** Extent of a chunk in face-uv units (the face spans -1..1). */
export function chunkExtent(level: number): number {
  return 2 / tilesPerEdge(level);
}

export interface ChunkBounds {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export function chunkBounds(id: ChunkId): ChunkBounds {
  const size = chunkExtent(id.level);
  const u0 = -1 + id.ix * size;
  const v0 = -1 + id.iy * size;
  return { u0, v0, u1: u0 + size, v1: v0 + size };
}

/** Unit direction at the centre of a chunk. */
export function chunkCentreDirection(id: ChunkId, out = new Vec3()): Vec3 {
  const b = chunkBounds(id);
  return faceToDirection(id.face, (b.u0 + b.u1) / 2, (b.v0 + b.v1) / 2, out);
}

/**
 * Approximate surface size of a chunk in metres.
 *
 * The cube-to-sphere projection is not area-preserving, so a chunk at a face
 * corner covers less surface than one at a face centre. The correction factor
 * matters for LOD selection: without it, corner chunks subdivide far more
 * aggressively than they need to and waste a third of the streaming budget.
 */
export function chunkSizeMetres(id: ChunkId, radius: number): number {
  const b = chunkBounds(id);
  const uc = (b.u0 + b.u1) / 2;
  const vc = (b.v0 + b.v1) / 2;
  // On the unprojected cube face the chunk is `extent` across at unit distance
  // from the centre; on the sphere that arc shrinks by 1/|(u,v,1)|.
  const distortion = 1 / Math.sqrt(uc * uc + vc * vc + 1);
  return chunkExtent(id.level) * radius * distortion;
}

/** The four children of a chunk. */
export function chunkChildren(id: ChunkId): [ChunkId, ChunkId, ChunkId, ChunkId] {
  const level = id.level + 1;
  const ix = id.ix * 2;
  const iy = id.iy * 2;
  return [
    { face: id.face, level, ix, iy },
    { face: id.face, level, ix: ix + 1, iy },
    { face: id.face, level, ix, iy: iy + 1 },
    { face: id.face, level, ix: ix + 1, iy: iy + 1 },
  ];
}

export function chunkParent(id: ChunkId): ChunkId | null {
  if (id.level === 0) return null;
  return { face: id.face, level: id.level - 1, ix: id.ix >> 1, iy: id.iy >> 1 };
}

/** The six root chunks. */
export function rootChunks(): ChunkId[] {
  return [0, 1, 2, 3, 4, 5].map((face) => ({ face: face as CubeFace, level: 0, ix: 0, iy: 0 }));
}

/** Locates the chunk at a given level containing a direction. */
export function chunkAtDirection(direction: Vec3, level: number): ChunkId {
  const fc = directionToFace(direction.normalized());
  const n = tilesPerEdge(level);
  const size = 2 / n;
  const ix = Math.min(n - 1, Math.max(0, Math.floor((fc.u + 1) / size)));
  const iy = Math.min(n - 1, Math.max(0, Math.floor((fc.v + 1) / size)));
  return { face: fc.face, level, ix, iy };
}

/**
 * Distance from a point to a chunk's spherical patch, in metres.
 *
 * Measuring to the *nearest* point of the patch rather than to its centre is
 * what makes the LOD criterion mean what it says. A centre measurement reports
 * a huge distance for the very chunk the camera is standing on, so it never
 * subdivides; a bounding-circle approximation errs the other way, reporting
 * every chunk as up to 40% nearer than it is, which inflates the number of
 * chunks drawn at every level by roughly the square of that error.
 *
 * The nearest point is found by clamping the observer's direction into the
 * chunk's own face coordinates. That clamp only exists when the observer is in
 * the same hemisphere as the face, so the patch's corners, edge midpoints and
 * centre are always tested as well — for a chunk on the far side of the body
 * one of those is the answer.
 */
export function distanceToChunk(
  id: ChunkId,
  observerFromCentre: Vec3,
  radius: number,
  chunkMaxRadius: number,
  scratch = new Vec3(),
): number {
  const observerRadius = observerFromCentre.length();
  if (observerRadius < 1e-6) return 0;

  // The patch is placed at its highest terrain: a chunk containing a mountain
  // is genuinely closer to a camera above it than the datum would suggest.
  const r = chunkMaxRadius > 0 ? chunkMaxRadius : radius;
  const b = chunkBounds(id);
  const uc = (b.u0 + b.u1) / 2;
  const vc = (b.v0 + b.v1) / 2;

  let bestSq = Infinity;
  const consider = (u: number, v: number): void => {
    const dir = faceToDirection(id.face, u, v, scratch);
    const dx = observerFromCentre.x - dir.x * r;
    const dy = observerFromCentre.y - dir.y * r;
    const dz = observerFromCentre.z - dir.z * r;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < bestSq) bestSq = d2;
  };

  const projected = projectOntoFace(id.face, observerFromCentre);
  if (projected) {
    consider(
      Math.min(b.u1, Math.max(b.u0, projected.u)),
      Math.min(b.v1, Math.max(b.v0, projected.v)),
    );
  }

  for (const u of [b.u0, uc, b.u1]) {
    for (const v of [b.v0, vc, b.v1]) consider(u, v);
  }

  return Math.sqrt(bestSq);
}

/**
 * True when a chunk lies entirely beyond the observer's horizon.
 *
 * A planet occludes its own far side, and on a body six thousand kilometres
 * across that is most of it. Without this test the streamer keeps — and draws —
 * a full sphere's worth of chunks while standing on the ground, which costs
 * hundreds of draw calls and a chunk budget that can never be met, all for
 * geometry no ray from the camera can reach.
 *
 * The test is angular. The observer can see `acos(occluder / observerRadius)`
 * of arc past their own zenith before the surface curves away; a chunk whose
 * highest terrain reaches `chunkMaxRadius` peeks a further
 * `acos(occluder / chunkMaxRadius)` back over that edge, which is why distant
 * mountains are visible from beyond the geometric horizon.
 *
 * @param occluderRadius radius of the sphere doing the occluding. Use the
 *   *lowest* possible terrain: a smaller occluder hides less, so the test stays
 *   conservative and never culls something that would have been visible.
 */
export function isBeyondHorizon(
  id: ChunkId,
  observerFromCentre: Vec3,
  occluderRadius: number,
  chunkMaxRadius: number,
  scratch = new Vec3(),
): boolean {
  const observerRadius = observerFromCentre.length();
  // Below the occluding surface (in a canyon, or inside the body) there is no
  // well-defined horizon, so cull nothing.
  if (observerRadius <= occluderRadius) return false;

  const dir = chunkCentreDirection(id, scratch);
  const cosAngle = Math.min(1, Math.max(-1, observerFromCentre.dot(dir) / observerRadius));
  const angle = Math.acos(cosAngle) - chunkExtent(id.level) * 0.75;
  if (angle <= 0) return false;

  const reach = Math.max(chunkMaxRadius, occluderRadius);
  const horizon = Math.acos(occluderRadius / observerRadius) + Math.acos(occluderRadius / reach);
  return angle > horizon;
}

/**
 * Maximum useful subdivision level for a body, chosen so the finest chunk has
 * roughly `targetVertexSpacing` metres between vertices.
 */
export function maxLevelFor(radius: number, verticesPerEdge: number, targetVertexSpacing = 3): number {
  const faceArc = (Math.PI * radius) / 2;
  const level = Math.log2(faceArc / (verticesPerEdge * targetVertexSpacing));
  return Math.max(0, Math.min(20, Math.round(level)));
}
